import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = (form.querySelector('input[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('提交成功，我们会尽快回复您！');
      form.reset();
      return;
    }

    setFormStatus('loading');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9opkn0senbniolesje0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言！我们会尽快与您联系。');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormStatus('error');
        setFormMessage(serverMsg || '提交失败，请稍后再试。');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查网络后重试。');
    }
  };

  const stats = [
    { value: '10000+', label: '影视资源' },
    { value: '98%', label: '用户好评率' },
    { value: '500万+', label: '月活跃用户' },
    { value: '24h', label: '资源更新周期' },
  ];

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于91免费 - 高清影视在线平台',
    description: '91免费致力于为广大影迷提供免费、高清、流畅的在线观影体验。了解我们的品牌故事、核心优势和联系方式。',
    url: 'https://91free.com/about',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://91free.com' },
        { '@type': 'ListItem', position: 2, name: '关于我们' },
      ],
    },
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="py-6">
              <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
            </div>

            <section className="mb-16">
              <div className="max-w-[700px]">
                <h1 className="text-2xl md:text-4xl font-bold text-foreground-900 mb-4 font-heading">
                  关于 <span className="text-primary-500">91免费</span>
                </h1>
                <p className="text-base text-foreground-600 leading-relaxed">
                  91免费是一个致力于为广大影迷提供免费、高清、流畅在线观影体验的影视平台。我们汇聚了海量优质影视资源，涵盖动作片、喜剧片、科幻片、爱情片、动漫等多种类型，满足不同用户的观影需求。
                </p>
              </div>
            </section>

            <section className="mb-16">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6">
                {stats.map((stat, index) => (
                  <div
                    key={index}
                    className="text-center p-5 md:p-6 rounded-xl bg-background-100 border border-background-200/70"
                  >
                    <div className="text-2xl md:text-3xl font-bold text-primary-500 mb-1 font-heading">
                      {stat.value}
                    </div>
                    <div className="text-xs md:text-sm text-foreground-600">{stat.label}</div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-16">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-6 font-heading">
                品牌故事
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div className="space-y-4">
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    91免费成立于2020年，由一群热爱电影的年轻人创立。创始团队深知优质影视内容对人们生活的重要性，但同时也看到了高昂的会员费用让许多观众望而却步的现实。正是基于"让每个人都能免费看好电影"的初心，91免费应运而生。
                  </p>
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    经过多年的发展，91免费已经成长为国内领先的免费影视平台之一。我们拥有超过10000部影视资源，涵盖国内外热门电影、经典老片、高分剧集和精品动漫。平台采用先进的流媒体技术和CDN加速服务，确保每位用户都能享受流畅的高清观影体验。
                  </p>
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    我们始终坚持以用户为中心的理念，不断优化产品体验，丰富内容库。未来，91免费将持续深耕影视领域，探索更多创新的观影方式，为更多用户带来优质的免费观影服务。
                  </p>
                </div>
                <div className="aspect-[4/3] rounded-xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20creative%20office%20workspace%20with%20team%20collaborating%2C%20film%20posters%20on%20walls%2C%20warm%20natural%20lighting%20through%20large%20windows%2C%20casual%20professional%20atmosphere%2C%20creative%20industry%20setting&width=800&height=600&seq=about-team-2025&orientation=landscape"
                    alt="91免费团队"
                    className="w-full h-full object-cover"
                    title="91免费团队"
                  />
                </div>
              </div>
            </section>

            <section className="mb-16">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-6 font-heading">
                核心优势
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {[
                  {
                    icon: 'ri-movie-2-line',
                    title: '海量资源',
                    desc: '超过10000部影视资源，涵盖动作、喜剧、科幻、爱情、动漫等多种类型，每日持续更新最新内容。',
                  },
                  {
                    icon: 'ri-4k-line',
                    title: '高清画质',
                    desc: '支持720P至4K多种画质选择，采用H.265编码技术，在保证高清画质的同时最大限度节省流量。',
                  },
                  {
                    icon: 'ri-speed-up-line',
                    title: '极速播放',
                    desc: '全球CDN加速节点覆盖，智能选择最优线路，秒开不卡顿，为用户带来流畅的观影体验。',
                  },
                  {
                    icon: 'ri-device-line',
                    title: '多端适配',
                    desc: '完美适配PC、平板、手机等各类设备，随时随地打开浏览器即可观看，无需下载任何APP。',
                  },
                  {
                    icon: 'ri-shield-check-line',
                    title: '安全可靠',
                    desc: '严格遵守网络安全规范，不收集用户隐私数据，所有资源经过安全检测，让您放心观影。',
                  },
                  {
                    icon: 'ri-customer-service-2-line',
                    title: '贴心服务',
                    desc: '7×24小时客服支持，随时解决您在观影过程中遇到的问题，倾听您的每一条建议。',
                  },
                ].map((item, index) => (
                  <div
                    key={index}
                    className="p-5 md:p-6 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300"
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 mb-4">
                      <i className={`${item.icon} text-xl`}></i>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-900 mb-2 font-heading">{item.title}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-16">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-6 font-heading">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
                {[
                  {
                    name: '张先生',
                    role: '资深影迷',
                    content: '在91免费看了一年多了，资源更新真的快，新片上映后没几天就能看到高清版。画质也很棒，4K看着太过瘾了！',
                    rating: 5,
                  },
                  {
                    name: '李女士',
                    role: '全职妈妈',
                    content: '孩子特别喜欢看动漫，91免费上的动漫资源特别全。而且不用注册就能看，太方便了。画质清晰，播放流畅。',
                    rating: 5,
                  },
                  {
                    name: '王同学',
                    role: '大学生',
                    content: '作为一个穷学生，91免费真的太友好了！完全免费，资源又多。宿舍四个人经常一起用它追剧看电影，推荐给身边所有同学了。',
                    rating: 5,
                  },
                ].map((review, index) => (
                  <div
                    key={index}
                    className="p-5 md:p-6 rounded-xl bg-background-100 border border-background-200/70"
                  >
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: review.rating }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-4">{review.content}</p>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">{review.name}</div>
                      <div className="text-xs text-foreground-500">{review.role}</div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-16" id="contact">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-4 font-heading">
                    联系我们
                  </h2>
                  <p className="text-sm text-foreground-600 mb-6 leading-relaxed">
                    如果您有任何问题、建议或合作意向，欢迎通过以下方式联系我们，或填写右侧表单，我们会尽快回复您。
                  </p>
                  <div className="flex flex-col gap-4">
                    {[
                      { icon: 'ri-mail-line', label: '电子邮箱', value: 'contact@91free.com' },
                      { icon: 'ri-phone-line', label: '客服电话', value: '400-888-9091' },
                      { icon: 'ri-map-pin-line', label: '公司地址', value: '北京市朝阳区影视产业园区A座1208室' },
                      { icon: 'ri-time-line', label: '工作时间', value: '周一至周日 9:00 - 21:00' },
                    ].map((item, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 flex-shrink-0">
                          <i className={item.icon}></i>
                        </div>
                        <div>
                          <div className="text-xs text-foreground-500 mb-0.5">{item.label}</div>
                          <div className="text-sm font-medium text-foreground-900">{item.value}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                    <h3 className="text-lg font-semibold text-foreground-900 mb-4 font-heading">发送消息</h3>
                    <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs text-foreground-600 mb-1.5">姓名</label>
                          <input
                            type="text"
                            name="name"
                            required
                            className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-300/60 rounded-lg text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                            placeholder="请输入您的姓名"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-foreground-600 mb-1.5">邮箱</label>
                          <input
                            type="email"
                            name="email"
                            required
                            className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-300/60 rounded-lg text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                            placeholder="请输入您的邮箱"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-foreground-600 mb-1.5">主题</label>
                        <input
                          type="text"
                          name="subject"
                          required
                          className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-300/60 rounded-lg text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                          placeholder="请输入消息主题"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-foreground-600 mb-1.5">消息内容</label>
                        <textarea
                          name="message"
                          required
                          rows={4}
                          maxLength={500}
                          className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-300/60 rounded-lg text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200 resize-none"
                          placeholder="请输入您的消息（最多500字）"
                        />
                      </div>
                      <div className="honeypot-wrapper">
                        <input
                          type="text"
                          name="phone_alt"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          readOnly
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={formStatus === 'loading'}
                        className="w-full py-3 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors duration-200 disabled:opacity-60 cursor-pointer whitespace-nowrap"
                      >
                        {formStatus === 'loading' ? '发送中...' : '发送消息'}
                      </button>
                      {formStatus === 'success' && (
                        <div className="flex items-center gap-2 text-sm text-secondary-700 bg-secondary-100 px-4 py-3 rounded-lg">
                          <i className="ri-checkbox-circle-line"></i>
                          {formMessage}
                        </div>
                      )}
                      {formStatus === 'error' && (
                        <div className="flex items-center gap-2 text-sm text-primary-700 bg-primary-100 px-4 py-3 rounded-lg">
                          <i className="ri-error-warning-line"></i>
                          {formMessage}
                        </div>
                      )}
                    </form>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}